/**
 * Get + update a single Binom landing — backs the edit dialog.
 *
 * For Media Devoted | Created By Ciro Autiero
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import api from '@/services/api';
import { binomKeys } from './useBinom';

export interface BinomLandingDetail {
  id: number | string;
  name: string;
  url?: string;
  languageCode?: string;
  groupUuid?: string;
  isDomainBanned?: boolean;
  [key: string]: unknown;
}

const landingKey = (id: string) =>
  [...binomKeys.all, 'landing', id] as const;

export function useBinomLanding(id: string | null) {
  return useQuery({
    queryKey: landingKey(id ?? ''),
    queryFn: async () => {
      const r = await api.get<{ data: BinomLandingDetail }>(
        `/binom/landings/${encodeURIComponent(id!)}`,
      );
      return r.data?.data ?? null;
    },
    enabled: Boolean(id),
    staleTime: 30_000,
  });
}

export function useUpdateBinomLanding(id: string | null) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (body: BinomLandingDetail) => {
      const r = await api.put<{ data: { success: boolean } }>(
        `/binom/landings/${encodeURIComponent(id!)}`,
        body,
      );
      return r.data?.data;
    },
    onSuccess: () => {
      if (id) qc.invalidateQueries({ queryKey: landingKey(id) });
    },
  });
}
